import { useState, useEffect } from 'react';
import {
  doc,
  getDoc,
  onSnapshot,
  collection,
  query,
  where,
  orderBy,
  type DocumentData,
} from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { useTenantStore } from '../../../stores/tenant.store';

export interface ApplicantDocument {
  fieldId: string;
  storagePath: string;
  fileName: string;
  uploadedAt: Date;
}

/** A reviewable document (admin- or applicant-uploaded) with its status. */
export interface ReviewableDocument {
  type: string;
  storagePath: string;
  verified: boolean;
  verifiedAt?: Date;
  rejectionReason?: string;
  /** Cuando el documento caduca. La compuerta de aceptación la mira, así que la
   *  ficha tiene que poder decir «vencido» en vez de un «verificado» que ya no
   *  es cierto. */
  expirationDate?: Date;
}

export interface DocumentRequirement {
  type: string;
  required: boolean;
  description?: string;
}

export interface HistoryEvent {
  id: string;
  action: string;
  actor: string;
  occurredAt: Date;
}

export interface AdmissionDetail {
  id: string;
  status: string;
  personId: string;
  personName: string;
  programId: string;
  programName: string;
  termId: string;
  termName: string;
  decisionNotes?: string;
  /** The committee approved but a rule blocked the automatic acceptance. */
  committeeApproval?: { blockedReason: string; blockedParams?: Record<string, unknown> };
  decidedBy?: string;
  decidedByName?: string;
  decidedAt?: Date;
  submittedAt?: Date;
  createdAt?: Date;
  updatedAt?: Date;
  personEmail?: string;
  personPhone?: string;
  // Applicant portal fields
  applicantToken?: string;
  invitedAt?: Date;
  applicantFormData?: Record<string, string | boolean>;
  applicantDocuments?: ApplicantDocument[];
  applicantCompletedAt?: Date;
  /** Merged admin + applicant documents with verification status (for the review UI). */
  reviewableDocuments?: ReviewableDocument[];
  /** Required/optional documents defined on the program. */
  documentRequirements?: DocumentRequirement[];
  /** Human label → fieldId map from the formTemplate */
  formTemplateFieldLabels?: Record<string, string>;
  /**
   * If present, a Firebase Auth user account has been provisioned
   * for this person. The applicant may or may not have activated it yet.
   */
  personAuthUid?: string;
}

// Label map for audit event action codes → readable Spanish labels
const ACTION_LABELS: Record<string, string> = {
  'admission.created': 'Solicitud creada',
  'admission.applicant_invited': 'Invitación enviada al postulante',
  'admission.form_completed': 'Formulario completado por el postulante',
  'admission.submitted': 'Solicitud enviada a revisión',
  'admission.review_started': 'Revisión iniciada',
  'admission.accepted': 'Solicitud aceptada',
  'admission.rejected': 'Solicitud rechazada',
  'admission.waitlisted': 'Postulante en lista de espera',
  'admission.withdrawn': 'Solicitud retirada',
  'admission.reset_to_draft': 'Solicitud revertida a borrador',
};

export function actionLabel(action: string): string {
  return ACTION_LABELS[action] ?? action;
}

export function useAdmissionDetail(admissionId: string | undefined) {
  const { currentTenantId } = useTenantStore();
  const [detail, setDetail] = useState<AdmissionDetail | null>(null);
  const [historyEvents, setHistoryEvents] = useState<HistoryEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ── Admission document subscription ─────────────────────────────────────
  useEffect(() => {
    if (!admissionId || !currentTenantId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const admissionRef = doc(db, `tenants/${currentTenantId}/admissions/${admissionId}`);

    const unsubscribe = onSnapshot(
      admissionRef,
      async (snap) => {
        try {
          if (!snap.exists()) {
            setError('Admisión no encontrada');
            setLoading(false);
            return;
          }

          const raw = snap.data() as DocumentData;

          const [personSnap, programSnap, termSnap] = await Promise.all([
            raw['personId']
              ? getDoc(doc(db, `tenants/${currentTenantId}/people/${raw['personId']}`))
              : Promise.resolve(null),
            raw['programId']
              ? getDoc(doc(db, `tenants/${currentTenantId}/programs/${raw['programId']}`))
              : Promise.resolve(null),
            raw['termId']
              ? getDoc(doc(db, `tenants/${currentTenantId}/terms/${raw['termId']}`))
              : Promise.resolve(null),
          ]);

          const personData = personSnap?.exists() ? personSnap.data() : null;
          const personName = personData
            ? `${personData['firstName'] ?? ''} ${personData['lastName'] ?? ''}`.trim()
            : (raw['personId'] ?? '—');

          const programData = programSnap?.exists() ? programSnap.data() : null;
          const programName = programData?.['name'] ?? raw['programId'] ?? '—';

          const termData = termSnap?.exists() ? termSnap.data() : null;
          const termName = termData?.['name'] ?? raw['termId'] ?? '—';

          // ── Resolve form field labels ─────────────────────────────
          // Prefer the snapshot frozen on the admission at submission time, so a
          // past application renders exactly as it was filled. Fall back to the
          // live template only for legacy admissions without a snapshot.
          let formTemplateFieldLabels: Record<string, string> | undefined;
          const snapshotFields = raw['formSnapshot'] as
            | Array<{ id: string; label: string }>
            | undefined;
          if (snapshotFields?.length) {
            formTemplateFieldLabels = Object.fromEntries(
              snapshotFields.map((f) => [f.id, f.label]),
            );
          } else {
            const formTemplateId = programData?.['formTemplateId'] as string | undefined;
            if (formTemplateId) {
              const templateSnap = await getDoc(
                doc(db, `tenants/${currentTenantId}/formTemplates/${formTemplateId}`),
              );
              if (templateSnap.exists()) {
                const fields = (templateSnap.data()['fields'] ?? []) as Array<{
                  id: string;
                  label: string;
                }>;
                formTemplateFieldLabels = Object.fromEntries(fields.map((f) => [f.id, f.label]));
              }
            }
          }

          const rawDocs = raw['applicantDocuments'] as Array<Record<string, unknown>> | undefined;
          const applicantDocuments: ApplicantDocument[] | undefined = rawDocs?.map((d) => ({
            fieldId: String(d['fieldId'] ?? ''),
            storagePath: String(d['storagePath'] ?? ''),
            fileName: String(d['fileName'] ?? ''),
            uploadedAt: (d['uploadedAt'] as { toDate?: () => Date })?.toDate?.() ?? new Date(),
          }));

          // Merged reviewable documents (admin + applicant) with status.
          const toReviewable = (arr: unknown): ReviewableDocument[] =>
            (arr as Array<Record<string, unknown>> | undefined)?.map((d) => {
              const vAt = (d['verifiedAt'] as { toDate?: () => Date })?.toDate?.();
              const expires = (d['expirationDate'] as { toDate?: () => Date })?.toDate?.();
              const reason = d['rejectionReason'] as string | undefined;
              return {
                type: String(d['type'] ?? ''),
                storagePath: String(d['storagePath'] ?? ''),
                verified: d['verified'] === true,
                ...(vAt ? { verifiedAt: vAt } : {}),
                ...(expires ? { expirationDate: expires } : {}),
                ...(reason ? { rejectionReason: reason } : {}),
              };
            }) ?? [];
          const reviewableDocuments: ReviewableDocument[] = [
            ...toReviewable(raw['documents']),
            ...toReviewable(raw['applicantDocuments']),
          ];
          const documentRequirements = (programData?.['documentRequirements'] ??
            []) as DocumentRequirement[];

          setDetail({
            id: snap.id,
            status: raw['status'] ?? 'draft',
            personId: raw['personId'] ?? '',
            personName,
            programId: raw['programId'] ?? '',
            programName,
            termId: raw['termId'] ?? '',
            termName,
            decisionNotes: raw['decisionNotes'],
            ...(raw['committeeApproval']?.blockedReason
              ? {
                  committeeApproval: {
                    blockedReason: raw['committeeApproval'].blockedReason as string,
                    ...(raw['committeeApproval'].blockedParams
                      ? { blockedParams: raw['committeeApproval'].blockedParams }
                      : {}),
                  },
                }
              : {}),
            decidedBy: raw['decidedBy'],
            decidedByName: raw['decidedByName'] ?? undefined,
            decidedAt: raw['decidedAt']?.toDate?.() ?? undefined,
            submittedAt: raw['submittedAt']?.toDate?.() ?? undefined,
            createdAt: raw['createdAt']?.toDate?.() ?? undefined,
            updatedAt: raw['updatedAt']?.toDate?.() ?? undefined,
            personEmail: personData?.['email'] ?? undefined,
            personPhone: personData?.['phone'] ?? undefined,
            applicantToken: raw['applicantToken'] ?? undefined,
            invitedAt: raw['invitedAt']?.toDate?.() ?? undefined,
            applicantFormData: raw['applicantFormData'] ?? undefined,
            ...(applicantDocuments !== undefined ? { applicantDocuments } : {}),
            reviewableDocuments,
            documentRequirements,
            applicantCompletedAt: raw['applicantCompletedAt']?.toDate?.() ?? undefined,
            ...(formTemplateFieldLabels !== undefined ? { formTemplateFieldLabels } : {}),
            personAuthUid: personData?.['authUid'] ?? undefined,
          });
        } catch (err) {
          console.error('[useAdmissionDetail] snapshot error:', err);
          setError('Error al cargar la admisión');
        } finally {
          setLoading(false);
        }
      },
      (err) => {
        console.error('[useAdmissionDetail] Firestore error:', err);
        setError('Error de conexión');
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [admissionId, currentTenantId]);

  // ── Audit events subscription — full real-time history ───────────────────
  useEffect(() => {
    if (!admissionId || !currentTenantId) return;

    const auditRef = collection(db, `tenants/${currentTenantId}/auditEvents`);
    const q = query(auditRef, where('targetId', '==', admissionId), orderBy('occurredAt', 'asc'));

    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        const events: HistoryEvent[] = snap.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            action: data['action'] ?? '',
            actor: data['actor'] ?? '',
            occurredAt: data['occurredAt']?.toDate?.() ?? new Date(),
          };
        });
        setHistoryEvents(events);
      },
      (err) => {
        console.error('[useAdmissionDetail] auditEvents error:', err);
      },
    );

    return () => unsubscribe();
  }, [admissionId, currentTenantId]);

  return { detail, historyEvents, loading, error };
}
